import { ErrorHandler } from '@angular/core';

/**
 * Handles Angular lazy-chunk load failures that happen when a new build is deployed
 * and the browser still holds a reference to old, no-longer-existing chunk filenames.
 *
 * Strategy: force a hard reload once so the browser fetches the fresh index.html
 * (which points to the new chunk hashes). A sessionStorage guard prevents reload
 * loops if the chunk is genuinely broken.
 */
const RELOAD_GUARD_KEY = 'chunk-reload-attempt';

export class LoadingChunkErrorHandler implements ErrorHandler {
    handleError(error: any): void {
        const message = error?.message || '';
        const name = error?.name || '';
        const chunkFailedMessage = /Loading chunk .* failed/i;
        const isChunkError =
            name === 'ChunkLoadError' ||
            chunkFailedMessage.test(message) ||
            /Loading chunk .* failed/i.test(error?.originalError?.message || '');

        if (isChunkError) {
            if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
                sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
                // Bypass HTTP cache to make sure index.html + new chunks are fetched fresh.
                window.location.reload();
                return;
            }
            // Already tried a reload in this session — surface the error instead of looping.
            console.error('ChunkLoadError persisted after reload attempt:', error);
            return;
        }

        // Successful navigation eventually clears the guard so future chunk errors can auto-recover.
        if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
            sessionStorage.removeItem(RELOAD_GUARD_KEY);
        }
        console.error(error);
    }
}
